import { CardData, PostIt, Project, IProjectMetrics } from '../types';
import { calculateMetrics } from './calculations';

// Interface para dados do relatório
interface ReportCard {
  id: string;
  title: string;
  description: string; // Notas vinculadas
  type: string;
  responsavel?: string;
  sistema?: string;
  tp?: number;
  tra?: number;
  pessoasSimultaneas?: number;
}

interface ReportPath {
  name: string;
  leadTime: number;
  percent: number;
}

interface ReportData {
  projectTitle: string;
  currentMap: {
    totalActivities: number;
    tra: number; // Total Lead Time (TRA)
    bottleneck: string;
    cards: ReportCard[];
    paths: ReportPath[];
  };
  futureMap: {
    totalActivities: number;
    tra: number;
    reductionPercent: number; // Comparado ao atual
    cards: ReportCard[];
  } | null;
  actions: { what: string; who: string; when: string; status: string }[];
}

/**
 * Gera relatório PDF Lean/VSM com mapa atual, futuro e plano de ação
 */
export const generateLeanReport = async (
  project: Project,
  postIts: PostIt[],
  mapElementId: string = 'canvas-content',
  futureMapElementId?: string
): Promise<void> => {
  const [{ default: html2canvas }, jsPdfModule] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  const JsPdfCtor = jsPdfModule.default ?? jsPdfModule.jsPDF;
  const doc = new JsPdfCtor('p', 'mm', 'a4'); // A4 Retrato
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  let yPos = 20; // Cursor vertical

  // Calcular métricas do estado atual
  const currentMetrics = calculateMetrics(project.estado_atual.cards);
  
  // Preparar cards do estado atual
  const currentCards: ReportCard[] = project.estado_atual.cards
    .filter(card => card.status === 'ativo' && card.tipo !== 'card_inicial' && card.tipo !== 'card_final')
    .map(card => {
      // Buscar post-its vinculados a este card
      const linkedPostIts = postIts.filter(p => p.fixedToNodeId === card.id);
      const notes = linkedPostIts.map(p => p.text).join(' | ');
      
      return {
        id: card.id,
        title: card.atividade || card.tipo || 'Sem título',
        description: notes || '-',
        type: card.tipo,
        responsavel: card.responsavel,
        sistema: card.sistema,
        tp: card.lt,
        tra: card.tt,
        pessoasSimultaneas: card.pessoasSimultaneas
      };
    });

  // Preparar caminhos (paths) - usar longestPath do metrics
  const paths: ReportPath[] = [];
  if (currentMetrics.longestPath) {
    paths.push({
      name: currentMetrics.longestPath,
      leadTime: parseFloat(currentMetrics.longestPathTRA || '0'),
      percent: parseFloat(currentMetrics.longestPathProb || '0')
    });
  }
  if (currentMetrics.shortestPath && currentMetrics.shortestPath !== currentMetrics.longestPath) {
    paths.push({
      name: currentMetrics.shortestPath,
      leadTime: parseFloat(currentMetrics.shortestPathTRA || '0'),
      percent: parseFloat(currentMetrics.shortestPathProb || '0')
    });
  }

  // Preparar ações do plano de ação (post-its do tipo 'action')
  const actionPostIts = postIts.filter(p => p.type === 'action' && p.actionPlan);
  const actions = actionPostIts.map(p => ({
    what: p.actionPlan?.oQue || p.text,
    who: p.actionPlan?.quem || '-',
    when: p.actionPlan?.quando || '-',
    status: 'Pendente' // TODO: adicionar campo de status no PostIt se necessário
  }));

  // Preparar dados do estado futuro (se existir)
  let futureMap: ReportData['futureMap'] = null;
  if (project.estado_futuro) {
    const futureMetrics = calculateMetrics(project.estado_futuro.cards);
    const futureCards: ReportCard[] = project.estado_futuro.cards
      .filter(card => card.status === 'ativo' && card.tipo !== 'card_inicial' && card.tipo !== 'card_final')
      .map(card => {
        const linkedPostIts = postIts.filter(p => p.fixedToNodeId === card.id);
        const notes = linkedPostIts.map(p => p.text).join(' | ');
        
        return {
          id: card.id,
          title: card.atividade || card.tipo || 'Sem título',
          description: notes || '-',
          type: card.tipo,
          responsavel: card.responsavel,
          sistema: card.sistema,
          tp: card.lt,
          tra: card.tt,
          pessoasSimultaneas: card.pessoasSimultaneas
        };
      });

    const currentTRA = parseFloat((currentMetrics.totalTT || currentMetrics.totalLTSum || 0).toString());
    const futureTRA = parseFloat((futureMetrics.totalTT || futureMetrics.totalLTSum || 0).toString());
    const reductionPercent = currentTRA > 0 ? ((currentTRA - futureTRA) / currentTRA * 100) : 0;

    futureMap = {
      totalActivities: futureCards.length,
      tra: futureTRA,
      reductionPercent: Math.round(reductionPercent * 10) / 10,
      cards: futureCards
    };
  }

  const reportData: ReportData = {
    projectTitle: project.nome_projeto,
    currentMap: {
      totalActivities: currentCards.length,
      tra: parseFloat((currentMetrics.totalTT || currentMetrics.totalLTSum || 0).toString()),
      bottleneck: currentMetrics.realBottleneck || 'Não identificado',
      cards: currentCards,
      paths: paths
    },
    futureMap: futureMap,
    actions: actions
  };

  // --- CAPA / PÁGINA 1: DADOS GERAIS DO MAPA ATUAL ---
  
  // Título
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text(reportData.projectTitle, pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;

  doc.setFontSize(16);
  doc.text("Mapa do Estado Atual (Current State)", margin, yPos);
  yPos += 10;

  // Métricas
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text(`Total de Atividades: ${reportData.currentMap.totalActivities}`, margin, yPos);
  yPos += 7;
  doc.text(`TRA (Lead Time Total): ${reportData.currentMap.tra.toFixed(2)} min/h`, margin, yPos);
  yPos += 7;
  doc.setTextColor(200, 0, 0); // Vermelho para gargalo
  doc.text(`Gargalo Crítico: ${reportData.currentMap.bottleneck}`, margin, yPos);
  doc.setTextColor(0, 0, 0); // Volta pro preto
  yPos += 10;

  // Tabela de Caminhos
  if (reportData.currentMap.paths.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.text("Análise de Caminhos e Ocorrências:", margin, yPos);
    yPos += 7;
    doc.setFont("helvetica", "normal");
    
    reportData.currentMap.paths.forEach(path => {
      doc.text(`- ${path.name}: ${path.leadTime.toFixed(2)} min (${path.percent.toFixed(1)}%)`, margin + 5, yPos);
      yPos += 6;
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    });
  }

  // --- PÁGINA 2: IMAGEM DO MAPA ATUAL (PAISAGEM) ---
  doc.addPage("a4", "l"); // Adiciona página Paisagem (Landscape)
  
  doc.setFontSize(14);
  doc.text("Visualização do Fluxo Atual", 15, 15);

  // Captura do DOM do mapa
  const mapElement = document.getElementById(mapElementId);
  if (mapElement) {
    try {
      const canvas = await html2canvas(mapElement, { 
        scale: 2, // Scale 2 melhora qualidade
        backgroundColor: '#ffffff',
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      // Ajuste de proporção para caber na folha A4 paisagem (297x210)
      const imgWidth = 260;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      const maxHeight = 150;
      const finalHeight = imgHeight > maxHeight ? maxHeight : imgHeight;
      doc.addImage(imgData, 'PNG', 15, 25, imgWidth, finalHeight);
    } catch (error) {
      console.error('Erro ao capturar mapa atual:', error);
      doc.text("Erro ao capturar imagem do mapa", 15, 50);
    }
  } else {
    doc.text("Elemento do mapa não encontrado", 15, 50);
  }

  // --- PÁGINAS 3+: LISTA DE POST-ITS (ATUAL) ---
  // Função auxiliar para desenhar cards
  const printCards = (cards: ReportCard[], title: string, showNotes: boolean) => {
    doc.addPage("a4", "p"); // Volta para Retrato
    yPos = 20;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(title, margin, yPos);
    yPos += 15;

    cards.forEach((card, index) => {
      // Verifica se cabe na página (5 por página aprox)
      if (index > 0 && index % 5 === 0) {
        doc.addPage();
        yPos = 20;
      }

      // Desenha o "Post-it" (Retângulo)
      doc.setFillColor(255, 255, 224); // Amarelo post-it claro
      doc.rect(margin, yPos, 40, 30, 'F'); // x, y, w, h
      doc.setDrawColor(0);
      doc.rect(margin, yPos, 40, 30, 'S'); // Borda

      // Texto dentro do Post-it
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      // splitTextToSize quebra o texto pra caber no box de 40mm
      const splitTitle = doc.splitTextToSize(card.title, 35);
      doc.text(splitTitle, margin + 2, yPos + 5);
      
      // Informações adicionais no post-it
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      if (card.responsavel) {
        doc.text(`Quem: ${card.responsavel}`, margin + 2, yPos + 12);
      }
      if (card.tp !== undefined) {
        doc.text(`TP: ${card.tp}`, margin + 2, yPos + 18);
      }
      if (card.tra !== undefined) {
        doc.text(`TRA: ${card.tra}`, margin + 2, yPos + 24);
      }

      // Se tiver notas (Futuro) ou detalhes ao lado
      if (showNotes && card.description && card.description !== '-') {
        doc.setFontSize(9);
        doc.text("Notas vinculadas:", margin + 50, yPos + 5);
        const splitNotes = doc.splitTextToSize(card.description, 120);
        doc.setFont("helvetica", "italic");
        doc.text(splitNotes, margin + 50, yPos + 10);
        doc.setFont("helvetica", "normal");
      }

      yPos += 45; // Espaço para o próximo item
      
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    });
  };

  // Imprime Cards Atuais (sem notas detalhadas ao lado)
  if (reportData.currentMap.cards.length > 0) {
    printCards(reportData.currentMap.cards, "Detalhamento: Cards do Fluxo Atual", false);
  }

  // --- PÁGINA X: RESUMO FUTURO ---
  if (reportData.futureMap) {
    doc.addPage("a4", "p");
    yPos = 20;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Mapa do Estado Futuro (Future State)", margin, yPos);
    yPos += 15;

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Total de Atividades: ${reportData.futureMap.totalActivities}`, margin, yPos);
    yPos += 8;
    doc.text(`Novo TRA Projetado: ${reportData.futureMap.tra.toFixed(2)} min/h`, margin, yPos);
    yPos += 8;
    
    // Box de Redução
    doc.setFillColor(220, 255, 220); // Verde claro
    doc.rect(margin, yPos, 80, 15, 'F');
    doc.setTextColor(0, 100, 0);
    doc.setFont("helvetica", "bold");
    doc.text(`Redução de Lead Time: -${reportData.futureMap.reductionPercent.toFixed(1)}%`, margin + 5, yPos + 10);
    doc.setTextColor(0);
    doc.setFont("helvetica", "normal");
    
    // --- PÁGINA Y: IMAGEM FUTURO (PAISAGEM) ---
    if (futureMapElementId) {
      doc.addPage("a4", "l");
      doc.setFontSize(14);
      doc.text("Visualização do Fluxo Futuro", 15, 15);
      
      const futureMapElement = document.getElementById(futureMapElementId);
      if (futureMapElement) {
        try {
          const canvas = await html2canvas(futureMapElement, { 
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 260;
          const imgHeight = (canvas.height / canvas.width) * imgWidth;
          const maxHeight = 150;
          const finalHeight = imgHeight > maxHeight ? maxHeight : imgHeight;
          doc.addImage(imgData, 'PNG', 15, 25, imgWidth, finalHeight);
        } catch (error) {
          console.error('Erro ao capturar mapa futuro:', error);
          doc.text("Erro ao capturar imagem do mapa futuro", 15, 50);
        }
      }
    }

    // --- PÁGINAS Z: LISTA DE POST-ITS (FUTURO) COM NOTAS ---
    if (reportData.futureMap.cards.length > 0) {
      printCards(reportData.futureMap.cards, "Detalhamento: Cards do Fluxo Futuro", true);
    }
  }

  // --- PÁGINA FINAL: PLANO DE AÇÃO ---
  if (reportData.actions.length > 0) {
    doc.addPage("a4", "p");
    yPos = 20;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Plano de Ação (Kaizen)", margin, yPos);
    yPos += 10;

    // Cabeçalho simples da tabela
    doc.setFontSize(10);
    doc.setFillColor(200);
    doc.rect(margin, yPos, 180, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text("O QUÊ", margin + 2, yPos + 5);
    doc.text("QUEM", margin + 80, yPos + 5);
    doc.text("PRAZO", margin + 120, yPos + 5);
    doc.text("STATUS", margin + 150, yPos + 5);
    yPos += 10;
    doc.setTextColor(0);

    // Linhas
    doc.setFont("helvetica", "normal");
    reportData.actions.forEach(action => {
      if (yPos > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
      const splitWhat = doc.splitTextToSize(action.what, 70);
      doc.text(splitWhat, margin + 2, yPos);
      doc.text(action.who, margin + 80, yPos);
      doc.text(action.when, margin + 120, yPos);
      doc.text(action.status, margin + 150, yPos);
      yPos += 8 + (splitWhat.length - 1) * 4; // Ajusta altura se texto quebrou
    });
  }

  // Salvar
  const fileName = `Relatorio_VSM_${reportData.projectTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`;
  doc.save(fileName);
};
